import { useEffect, useState } from "react";
import { UserScript } from "../config";

const useScriptLoader = (scripts: UserScript[]) => {
	const [scriptsLoaded, setScriptsLoaded] = useState(false);

	useEffect(() => {
		if (scripts.length === 0) {
			setScriptsLoaded(true);
			return;
		}

		let loadedCount = 0;
		const autoBlockingScript = scripts.filter((script) => script.loadFirst);
		const scriptElements: HTMLScriptElement[] = [];

		// load scripts sequentially,
		const loadScripts = (scriptConfigs: UserScript[]) => {
			scriptConfigs.forEach((scriptConfig) => {
				const scriptElement = document.createElement("script");

				// set each attribute from UserScript
				Object.keys(scriptConfig).forEach((key) => {
					if (key === "loadFirst") return; // skip custom attribute

					if (typeof scriptConfig[key] === "string") {
						// set string attribute (e.g. "src", "id", "crossOrigin")
						scriptElement.setAttribute(key, String(scriptConfig[key]));
					} else if (
						typeof scriptConfig[key] === "boolean" &&
						scriptConfig[key] === true
					) {
						// set boolean attribute (e.g. "async", "defer")
						scriptElement.setAttribute(key, "");
					}
				});

				scriptElement.onload = () => {
					loadedCount++;
					if (loadedCount === scripts.length) setScriptsLoaded(true);
				};
				scriptElement.onerror = () => {
					loadedCount++;
					if (loadedCount === scripts.length) setScriptsLoaded(true);
				};
				scriptElements.push(scriptElement);

				// If loadFirst is true, insert after <title> in <head>
				if (scriptConfig.loadFirst && document.head) {
					const head = document.head;
					const titleEl = head.querySelector("title");
					if (titleEl && titleEl.nextSibling) {
						head.insertBefore(scriptElement, titleEl.nextSibling);
					} else if (titleEl) {
						head.appendChild(scriptElement);
					} else {
						head.insertBefore(scriptElement, head.firstChild);
					}
				} else {
					document.body.appendChild(scriptElement);
				}
			});
		};

		if (autoBlockingScript.length > 0) {
			// load autoBlocking script first
			loadScripts(autoBlockingScript);
		}

		// skip loadFirst scripts
		loadScripts(scripts.filter((script) => !script.loadFirst));

		return () => {
			scriptElements.forEach((script) => {
				if (script.parentNode) {
					script.parentNode.removeChild(script);
				}
			});
		};
	}, [scripts]);

	return { scriptsLoaded };
};

export default useScriptLoader;
